import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiFileText, FiLogOut, FiSearch, FiPackage, FiAlertOctagon, FiShield } from "react-icons/fi";
import { HiOutlineWallet } from "react-icons/hi2";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  MdAccountCircle,
  MdDownload,
  MdOutlineShoppingBag
} from "react-icons/md";
import { RiMoneyDollarBoxLine } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { assets } from "../../Assets/assets";
import { useAuth } from "../Context/AuthContext";
import { useNotifications } from "../Context/NotificationContext";
import { useWallet } from "../Context/WalletContext";
import { useClickOutside } from "../Hooks/useClickOutside";
import { useLockBodyScroll } from "../Hooks/useLockBodyScroll";
import useProductVarietiesCache from "../Hooks/useProductVarietiesCache";
import CommonButton from "./CommonButton";
import NotificationDrawer from "./NotificationDrawer";
import SearchModal from "./SearchModal";
import TopMarquee from "./TopMarquee";
import { Wallet2 } from "lucide-react";
import { GiReturnArrow } from "react-icons/gi";

const UserNavBar = ({ showNav }) => {
  const { walletBalance } = useWallet();
  const { isAuthenticated, logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const pathname = window.location.pathname;
  const navigate = useNavigate();

  // ✅ NEW: Use the cache hook for product varieties
  const { search: searchVarieties, searchResults, loading: varietiesLoading } = useProductVarietiesCache
    ();

  const [openMenu, setOpenMenu] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const menuRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const loginDropdownRef = useClickOutside(() => setLoginDropdownOpen(false));
  const searchRef = useClickOutside(() => {
    setShowDropdown(false);
    setResults([]);
    setSelectedIndex(-1);
  });
  const dontScroll = openMenu || showSearch;
  useLockBodyScroll(dontScroll);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [query, results]);

  const handleSelectVariety = useCallback(
    (item) => {
      if (!item) return;
      const categoryId =
        item.productId?.subCategoryId?.categoryId?._id ||
        item.productId?.subCategoryId?.categoryId ||
        "";
      const subCategoryId =
        item.productId?.subCategoryId?._id ||
        item.productId?.subCategoryId ||
        "";
      const productId = item.productId?._id || item.productId || "";

      navigate(
        `/products?category=${categoryId}&subCategory=${subCategoryId}&product=${productId}&variety=${item._id}`
      );
      setQuery("");
      setResults([]);
      setSelectedIndex(-1);
      setShowDropdown(false);
    },
    [navigate]
  );

  const handleKeyDown = (e) => {
    if (!results || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter") {
      if (selectedIndex >= 0 && selectedIndex < results.length) {
        e.preventDefault();
        handleSelectVariety(results[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setResults([]);
      setSelectedIndex(-1);
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.children[selectedIndex];
      if (selectedElement && typeof selectedElement.scrollIntoView === "function") {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  // Dismiss navbar search dropdown on outer window scroll
  useEffect(() => {
    if (results.length === 0 && !showDropdown) return;

    const handleOuterScroll = () => {
      setShowDropdown(false);
      setResults([]);
      setSelectedIndex(-1);
    };

    window.addEventListener("scroll", handleOuterScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleOuterScroll);
    };
  }, [results, showDropdown]);

  // ✅ UPDATED: Frontend search instead of backend
  const performSearch = useCallback((searchQuery) => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setResults([]);
      return;
    }

    // ✅ Use frontend search with fuzzy matching
    const searchedResults = searchVarieties(trimmedQuery);
    setResults(searchedResults || []);
  }, [searchVarieties]);

  const debouncedSearch = useCallback(
    debounce((q) => performSearch(q), 150),
    [performSearch]
  );

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };

    if (openMenu) {
      document.addEventListener("mousedown", handleMouseDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [openMenu]);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length >= 1) {
      setShowDropdown(true);
      debouncedSearch(trimmed);
    } else {
      setResults([]);
      setShowDropdown(false);
    }

    return () => debouncedSearch.cancel();
  }, [query, debouncedSearch]);

  return (
    <>
      <nav data-aos="fade-down"
        className={`w-full fixed z-50 top-0 shadow-sm bg-[#FFFCFC] transition-transform duration-300 ${showNav ? "translate-y-0" : "-translate-y-full md:translate-y-0"
          }`}
      >
        {!isAuthenticated && <TopMarquee />}
        <div className="flex items-center justify-between 2xl:max-w-7xl mx-auto 2xl:px-0 px-4 md:px-8 py-1">
          <div className="flex items-center gap-2">
            {/* LEFT - LOGO */}
            <img
              onClick={() => navigate("/")}
              src={assets.qqtlogo}
              alt="logo"
              className="w-[7rem] md:w-[6rem] h-full cursor-pointer"
            />
          </div>

          {!isAuthenticated ? (
            <div className="flex items-center gap-3 sm:gap-6 flex-1 justify-end">

              {/* ✅ SEARCH BAR - BEFORE LOGIN ──────────────────────────────────────── */}
              <div ref={searchRef} className="relative w-full md:w-[320px] lg:w-[460px] xl:w-[540px]">
                <div className="flex items-center bg-[#F7F8FB] rounded-xl md:px-3.5 md:py-2 border border-gray-200/80 focus-within:border-primaryColor/40 focus-within:ring-2 focus-within:ring-primaryColor/10 transition shadow-2xs">
                  {/* MOBILE SEARCH ICONs */}
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      setQuery("");
                      setResults([]);
                      setShowSearch(true);
                    }}
                    className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-primaryColor/10 transition"
                  >
                    🔍
                  </button>

                  {/* DESKTOP SEARCH */}
                  <FiSearch className="text-primaryTextColor mr-2 hidden md:block text-lg flex-shrink-0" />

                  <input
                    type="text"
                    placeholder="Search product varieties..."
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      if (e.target.value.trim()) setShowDropdown(true);
                    }}
                    onFocus={() => {
                      if (query.trim()) setShowDropdown(true);
                    }}
                    onKeyDown={handleKeyDown}
                    className="bg-transparent hidden md:block outline-none text-sm w-full text-gray-800 font-medium placeholder-gray-400"
                  />

                  {query && (
                    <button
                      onClick={() => {
                        setQuery("");
                        setResults([]);
                        setSelectedIndex(-1);
                        setShowDropdown(false);
                      }}
                      className="hidden md:block text-gray-400 hover:text-gray-600 transition"
                    >
                      ✖
                    </button>
                  )}
                </div>

                {/* ✅ DROPDOWN (DESKTOP ONLY) ──────────────────────── */}
                {showDropdown && query.trim().length >= 1 && (
                  <div className="hidden md:block absolute top-full left-0 right-0 min-w-[340px] md:min-w-[460px] lg:min-w-[540px] mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-[420px] overflow-y-auto overscroll-contain z-50 p-2">
                    {/* ✅ LOADING STATE */}
                    {varietiesLoading && (
                      <div className="flex justify-center py-6">
                        <div className="w-6 h-6 border-2 border-primaryColor border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}

                    {/* ✅ RESULTS - FRONTEND SEARCH */}
                    {!varietiesLoading && results.length > 0 && (
                      <div ref={resultsContainerRef} className="space-y-1">
                        {results.map((item, idx) => (
                          <div
                            key={item._id}
                            onClick={() => handleSelectVariety(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className={`flex items-start gap-3 p-2.5 rounded-xl cursor-pointer transition border ${idx === selectedIndex
                              ? "bg-primaryColor/10 border-primaryColor/30 ring-1 ring-primaryColor/20"
                              : "hover:bg-primaryColor/5 border-transparent hover:border-primaryColor/10"
                              } group`}
                          >
                            <img
                              src={item.img}
                              alt={item.varietyName}
                              className="w-11 h-11 rounded-xl object-cover flex-shrink-0 border border-gray-200 shadow-2xs mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 leading-snug break-words group-hover:text-primaryColor transition">
                                {item.varietyName}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {item.productId?.name || "Product"} {item.productId?.subCategoryId?.name ? `• ${item.productId.subCategoryId.name}` : ""}
                              </p>
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {item.grade && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                                    Grade: {item.grade}
                                  </span>
                                )}
                                {(item.slabAdjustedMOQ || item.minimum_MOQ) && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    MOQ: {item.slabAdjustedMOQ || item.minimum_MOQ} {item.standardizedUNIT || "Kg"}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ✅ NO RESULTS */}
                    {!varietiesLoading &&
                      query.trim().length >= 1 &&
                      results.length === 0 && (
                        <div className="text-center py-6 text-sm text-gray-500">
                          No product varieties found
                        </div>
                      )}
                  </div>
                )}
              </div>

              {pathname !== "/application-download" && (
                <>
                  <Link
                    to="/application-download"
                    className="text-sm hidden sm:block font-semibold text-primaryColor"
                  >
                    Download App
                  </Link>
                  <Link
                    to="/application-download"
                    className="text-sm block font-semibold sm:hidden text-primaryColor"
                  >
                    App <MdDownload className="inline-block" />
                  </Link>
                </>
              )}

              {/* DESKTOP (lg and above): Dual Separate Buttons */}
              <div className="hidden lg:flex items-center gap-2">
                <CommonButton
                  onClick={() => navigate("/login?role=buyer")}
                  label={"Log In / Sign Up as Buyer"}
                />
                <button
                  type="button"
                  onClick={() => navigate("/login?role=supplier")}
                  className="bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl shadow-xs transition duration-200 cursor-pointer whitespace-nowrap"
                >
                  Log In / Sign Up as Supplier
                </button>
              </div>

              {/* MOBILE & TABLET (below lg): Dropdown Button */}
              <div ref={loginDropdownRef} className="relative lg:hidden">
                <button
                  type="button"
                  onClick={() => setLoginDropdownOpen((prev) => !prev)}
                  className="bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl shadow-xs transition duration-200 cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                >
                  Log In / Sign Up
                  <span className={`text-[10px] transition-transform duration-200 ${loginDropdownOpen ? "rotate-180" : ""}`}>
                    ▼
                  </span>
                </button>

                {loginDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <button
                      type="button"
                      onClick={() => {
                        setLoginDropdownOpen(false);
                        navigate("/login?role=buyer");
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-gray-800 hover:bg-primaryColor/10 hover:text-primaryColor transition text-left"
                    >
                      <span className="text-base">🛒</span>
                      Buyer
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLoginDropdownOpen(false);
                        navigate("/login?role=supplier");
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-gray-800 hover:bg-primaryColor/10 hover:text-primaryColor transition text-left mt-1"
                    >
                      <span className="text-base">🏢</span>
                      Supplier
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center w-full justify-end">
              {/* ✅ SEARCH BAR - AFTER LOGIN ──────────────────────────────────────── */}
              <div ref={searchRef} className="relative w-full md:w-[360px] lg:w-[480px] xl:w-[560px] mr-4">
                <div className="flex items-center bg-[#F7F8FB] rounded-xl md:px-3.5 md:py-2 border border-gray-200/80 focus-within:border-primaryColor/40 focus-within:ring-2 focus-within:ring-primaryColor/10 transition shadow-2xs">
                  {/* MOBILE SEARCH ICON */}
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      setQuery("");
                      setResults([]);
                      setShowSearch(true);
                    }}
                    className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-primaryColor/10 transition"
                  >
                    🔍
                  </button>

                  {/* DESKTOP SEARCH */}
                  <FiSearch className="text-primaryTextColor mr-2 hidden md:block text-lg flex-shrink-0" />

                  <input
                    type="text"
                    placeholder="Search product varieties..."
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      if (e.target.value.trim()) setShowDropdown(true);
                    }}
                    onFocus={() => {
                      if (query.trim()) setShowDropdown(true);
                    }}
                    onKeyDown={handleKeyDown}
                    className="bg-transparent hidden md:block outline-none text-sm w-full text-gray-800 font-medium placeholder-gray-400"
                  />

                  {query && (
                    <button
                      onClick={() => {
                        setQuery("");
                        setResults([]);
                        setSelectedIndex(-1);
                        setShowDropdown(false);
                      }}
                      className="hidden md:block text-gray-400 hover:text-gray-600 transition"
                    >
                      ✖
                    </button>
                  )}
                </div>

                {/* ✅ DROPDOWN (DESKTOP ONLY) ──────────────────────── */}
                {showDropdown && query.trim().length >= 1 && (
                  <div className="hidden md:block absolute top-full left-0 right-0 min-w-[340px] md:min-w-[460px] lg:min-w-[540px] mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-[420px] overflow-y-auto overscroll-contain z-50 p-2">
                    {/* ✅ LOADING STATE */}
                    {varietiesLoading && (
                      <div className="flex justify-center py-6">
                        <div className="w-6 h-6 border-2 border-primaryColor border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}

                    {/* ✅ RESULTS - FRONTEND SEARCH */}
                    {!varietiesLoading && results.length > 0 && (
                      <div ref={resultsContainerRef} className="space-y-1">
                        {results.map((item, idx) => (
                          <div
                            key={item._id}
                            onClick={() => handleSelectVariety(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className={`flex items-start gap-3 p-2.5 rounded-xl cursor-pointer transition border ${idx === selectedIndex
                              ? "bg-primaryColor/10 border-primaryColor/30 ring-1 ring-primaryColor/20"
                              : "hover:bg-primaryColor/5 border-transparent hover:border-primaryColor/10"
                              } group`}
                          >
                            <img
                              src={item.img}
                              alt={item.varietyName}
                              className="w-11 h-11 rounded-xl object-cover flex-shrink-0 border border-gray-200 shadow-2xs mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 leading-snug break-words group-hover:text-primaryColor transition">
                                {item.varietyName}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {item.productId?.name || "Product"} {item.productId?.subCategoryId?.name ? `• ${item.productId.subCategoryId.name}` : ""}
                              </p>
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {item.grade && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                                    Grade: {item.grade}
                                  </span>
                                )}
                                {(item.slabAdjustedMOQ || item.minimum_MOQ) && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    MOQ: {item.slabAdjustedMOQ || item.minimum_MOQ} {item.standardizedUNIT || "Kg"}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ✅ NO RESULTS */}
                    {!varietiesLoading &&
                      query.trim().length >= 1 &&
                      results.length === 0 && (
                        <div className="text-center py-6 text-sm text-gray-500">
                          No product varieties found
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* WALLET */}
              <div
                onClick={() => navigate("/wallet")}
                className="flex items-center gap-1.5 mr-3 cursor-pointer"
                style={{ transition: "opacity 0.15s" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <div className="flex items-center gap-1.5 bg-primaryColor pl-1.5 pr-3 py-1 rounded-full">

                  {/* ₹ bubble */}
                  <div
                    className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(255,255,255,0.18)" }}
                  >
                    <Wallet2 size={16} style={{ color: "white" }} />
                  </div>

                  {/* balance */}
                  <span className="text-[12px] font-medium text-white tracking-wide whitespace-nowrap">
                    ₹ {walletBalance !== null
                      ? Math.round(walletBalance).toLocaleString("en-IN")
                      : "—"}
                  </span>
                </div>
              </div>

              {/* NOTIFICATIONS */}
              <div className="relative mr-2">
                <IoMdNotificationsOutline
                  size={25}
                  onClick={() => setIsNotificationOpen(true)}
                  className="text-primaryColor cursor-pointer dark:text-darkModeTextColor"
                />
                {unreadCount > 0 && (
                  <span className={`absolute px-0.5 -top-[0.1rem] ${unreadCount > 9 ? "-right-1" : "right-[0.1rem]"} bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center`}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>

              {/* PROFILE CHIP + DROPDOWN */}
              <div
                ref={menuRef}
                className="relative flex items-center gap-2 cursor-pointer rounded-xl px-2 py-1 hover:bg-primaryColor/8 transition-all duration-200 group"
                onClick={() => {
                  setOpenMenu((prev) => !prev);
                }}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {user?.profilePic ? (
                    <img
                      src={user.profilePic}
                      alt={user?.fullName || "Profile"}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-primaryColor/20 group-hover:ring-primaryColor/40 transition-all"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primaryColor/10 flex items-center justify-center ring-2 ring-primaryColor/20 group-hover:ring-primaryColor/40 transition-all">
                      <MdAccountCircle className="text-xl text-primaryColor" />
                    </div>
                  )}
                  {/* Online dot */}
                  <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-400 rounded-full ring-1 ring-white" />
                </div>

                {/* Name + date — desktop only */}
                <div className="hidden md:flex flex-col items-start leading-tight">
                  <span className="text-xs font-semibold text-gray-800 max-w-[90px] truncate">
                    {user?.fullName?.split(" ")[0] || "Account"}
                  </span>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {new Date().toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>

                {/* DROPDOWN — desktop only */}
                <div
                  className={`absolute right-0 top-full mt-3 w-44 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-200 ease-out
      ${openMenu ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}`}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/account"); setOpenMenu(false); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                  >
                    <MdAccountCircle className="text-lg text-primaryColor" /> Account
                  </button>
                  {user?.role === "supplier" ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/supplier-orders");
                          setOpenMenu(false);
                        }}
                        className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                      >
                        <MdOutlineShoppingBag className="text-lg text-primaryColor" /> Live Orders
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/supplier-rqm-requests");
                          setOpenMenu(false);
                        }}
                        className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                      >
                        <FiFileText className="text-lg text-primaryColor" /> RQM Requests
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/supplier-linked-varieties");
                          setOpenMenu(false);
                        }}
                        className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                      >
                        <FiPackage className="text-lg text-primaryColor" /> Linked Products
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/supplier-penalties");
                          setOpenMenu(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition text-amber-700 font-medium"
                      >
                        <FiAlertOctagon className="text-lg text-amber-600" /> Penalties
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/quotations");
                          setOpenMenu(false);
                        }}
                        className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                      >
                        <RiMoneyDollarBoxLine className="text-lg text-primaryColor" /> Quotations
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/orders");
                          setOpenMenu(false);
                        }}
                        className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                      >
                        <MdOutlineShoppingBag className="text-lg text-primaryColor" /> Orders
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/buyer-penalties");
                          setOpenMenu(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition text-amber-700 font-medium"
                      >
                        <FiAlertOctagon className="text-lg text-amber-600" /> Penalties
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/returns"); setOpenMenu(false); }}
                    className="hidden md:flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                  >
                    <GiReturnArrow className="text-lg text-primaryColor" /> Returns
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/wallet"); setOpenMenu(false); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                  >
                    <HiOutlineWallet className="text-lg text-primaryColor" /> Wallet
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/gate-passes"); setOpenMenu(false); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                  >
                    <FiShield className="text-lg text-primaryColor" /> Gate-Passes
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/declarations"); setOpenMenu(false); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-primaryColor/10 transition"
                  >
                    <FiFileText className="text-lg text-primaryColor" /> Declarations
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); logout(); setOpenMenu(false); navigate("/"); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition"
                  >
                    <FiLogOut className="text-lg text-primaryColor" /> Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Modals */}
      <SearchModal show={showSearch} onClose={() => setShowSearch(false)} />

      <NotificationDrawer
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
    </>
  );
};

export default UserNavBar;